import type { SubmissionWithPicks } from '@/lib/queries'
import type { Attendee } from '@/types/database'

/**
 * Expanding one submission into who that person picked.
 *
 * The stored selection is only an attendee id, which tells an organiser
 * nothing. This resolves each one to a person, and — the question actually
 * being asked when someone opens a submission — says whether the feeling was
 * mutual.
 *
 * Mutuality is computed from the submissions as they stand, using the same rule
 * as the matchmaker: A and B are mutual when each one's *newest* entry selects
 * the other. That means it can disagree with a saved match run if someone has
 * submitted since, which is the honest thing to show — the run is a snapshot,
 * this is now.
 */

export interface SubmissionPick {
  attendeeId: string
  /** Blank when the attendee has since been removed. */
  matchmakingId: string
  name: string
  email: string | null
  phone: string | null
  /** That person's newest entry picked the submitter back. */
  mutual: boolean
  /**
   * That person picked others but not the submitter. Distinguished from "has
   * not submitted at all", because they mean different things to an organiser
   * chasing people up.
   */
  pickedOthers: boolean
  /** They have not submitted, so nothing is known about their picks yet. */
  awaitingSubmission: boolean
  /** The attendee no longer exists — removed after this submission was made. */
  missing: boolean
}

export interface SubmissionDetail {
  picks: SubmissionPick[]
  mutualCount: number
}

function displayName(attendee: Attendee): string {
  return `${attendee.first_name} ${attendee.last_name ?? ''}`.trim()
}

/**
 * Everyone's entries, newest first, keyed by who made them.
 *
 * Submitting again adds an entry instead of replacing one, so this is how the
 * screens tell a person's current answer from the ones they superseded by
 * sending another. Recency decides, exactly as it does in the matchmaker; the
 * id breaks a tie so the order never depends on how the rows came back.
 */
export function groupBySubmitter(
  submissions: readonly SubmissionWithPicks[]
): Map<string, SubmissionWithPicks[]> {
  const grouped = new Map<string, SubmissionWithPicks[]>()
  for (const submission of submissions) {
    const existing = grouped.get(submission.submitter_attendee_id)
    if (existing === undefined) grouped.set(submission.submitter_attendee_id, [submission])
    else existing.push(submission)
  }

  for (const entries of grouped.values()) {
    entries.sort((a, b) => {
      const byTime = b.submitted_at.localeCompare(a.submitted_at)
      return byTime !== 0 ? byTime : b.id.localeCompare(a.id)
    })
  }

  return grouped
}

/** The entry the matchmaker would read for each person. */
export function newestBySubmitter(
  submissions: readonly SubmissionWithPicks[]
): Map<string, SubmissionWithPicks> {
  const newest = new Map<string, SubmissionWithPicks>()
  for (const [attendeeId, entries] of groupBySubmitter(submissions)) {
    const first = entries[0]
    if (first !== undefined) newest.set(attendeeId, first)
  }
  return newest
}

export function describeSubmissionPicks(
  submission: SubmissionWithPicks,
  allSubmissions: readonly SubmissionWithPicks[],
  attendees: readonly Attendee[]
): SubmissionDetail {
  const attendeeById = new Map(attendees.map((a) => [a.id, a]))

  // Only each person's newest entry, because that is the one the matchmaker
  // reads. An earlier entry of theirs says nothing about who they would be
  // matched with today.
  const newestByAttendee = newestBySubmitter(allSubmissions)

  const picks: SubmissionPick[] = submission.selections
    .slice()
    // Position is the order they were chosen on the form; null sorts last.
    .sort(
      (a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER)
    )
    .map((selection) => {
      const attendee = attendeeById.get(selection.selected_attendee_id)
      const theirSubmission = newestByAttendee.get(selection.selected_attendee_id)

      const mutual =
        theirSubmission?.selections.some(
          (s) => s.selected_attendee_id === submission.submitter_attendee_id
        ) ?? false

      return {
        attendeeId: selection.selected_attendee_id,
        matchmakingId: attendee?.matchmaking_id ?? '',
        name: attendee ? displayName(attendee) : 'Removed attendee',
        // Prefer what the person typed on the form over the imported record,
        // exactly as get_match_pairs does. Eventbrite rarely supplies a phone,
        // so reading only the attendee row showed "—" for people who had in
        // fact given their number — and disagreed with the matches table about
        // the same person.
        email: theirSubmission?.email ?? attendee?.email ?? null,
        phone: theirSubmission?.phone ?? attendee?.phone ?? null,
        mutual,
        pickedOthers: theirSubmission !== undefined && !mutual,
        awaitingSubmission: theirSubmission === undefined,
        missing: attendee === undefined,
      }
    })

  return {
    picks,
    mutualCount: picks.filter((pick) => pick.mutual).length,
  }
}
